import { useEffect, useRef, useState } from 'react';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  cancelRecording,
  getRecorder,
  isRecorderSupported,
  startRecording,
  stopRecording,
  subscribeRecorder,
  type RecorderState,
  type RecordingResult,
} from '@/lib/voiceRecorder';

/**
 * Telegram-style hold-to-record gesture, extracted from the Composer.
 *
 * Press & hold the mic → record; release inside `rootRef` → emit the
 * recording via `onResult`; release with the pointer OUTSIDE → cancel. A tap
 * that ends before getUserMedia resolves cancels the recording the moment it
 * starts, so an accidental click never leaves the mic open. Sub-500ms takes
 * are dropped (accidental tap) rather than emitted.
 *
 * Returns the live recorder state (for the HUD/button), the `willCancel` flag
 * (pointer in the cancel zone), and the pointer handlers to spread on the mic.
 */
const MIN_DURATION_MS = 500;
/** Slack so a tiny jitter near the edge doesn't arm cancel. */
const CANCEL_MARGIN_PX = 24;

/** True when (x,y) sits more than `margin` px outside `rect` — the cancel zone. */
export function isOutsideCancelZone(
  rect: { left: number; right: number; top: number; bottom: number },
  x: number,
  y: number,
  margin = CANCEL_MARGIN_PX,
): boolean {
  return (
    x < rect.left - margin ||
    x > rect.right + margin ||
    y < rect.top - margin ||
    y > rect.bottom + margin
  );
}

export function useHoldToRecord(opts: {
  rootRef: React.RefObject<HTMLElement | null>;
  onResult: (result: RecordingResult) => void | Promise<void>;
}) {
  const { rootRef } = opts;
  const [recorder, setRecorder] = useState<RecorderState>(() => getRecorder());
  useEffect(() => subscribeRecorder(setRecorder), []);

  const holdingRef = useRef(false);
  const cancelArmedRef = useRef(false);
  // Released before getUserMedia resolved — cancel as soon as it starts.
  const pendingCancelRef = useRef(false);
  const [willCancel, setWillCancel] = useState(false);

  // Keep the latest callback without re-subscribing handlers.
  const onResultRef = useRef(opts.onResult);
  onResultRef.current = opts.onResult;

  useEffect(() => {
    if (recorder.kind === 'recording' && pendingCancelRef.current) {
      pendingCancelRef.current = false;
      cancelRecording();
    }
  }, [recorder.kind]);

  async function finishAndEmit() {
    if (recorder.kind !== 'recording') return;
    const result = await stopRecording();
    if (!result || result.durationMs < MIN_DURATION_MS) {
      if (result && result.durationMs < MIN_DURATION_MS) {
        toast.message(i18n.t('composer.recordTooShort'));
      }
      return;
    }
    await onResultRef.current(result);
  }

  function onPointerDown(e: React.PointerEvent) {
    if (recorder.kind === 'recording' || holdingRef.current) return;
    if (!isRecorderSupported()) {
      toast.error(i18n.t('lib2.voice.recordUnsupported'));
      return;
    }
    e.preventDefault();
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* capture optional */
    }
    holdingRef.current = true;
    cancelArmedRef.current = false;
    pendingCancelRef.current = false;
    setWillCancel(false);
    void startRecording();
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!holdingRef.current) return;
    const root = rootRef.current;
    if (!root) return;
    const outside = isOutsideCancelZone(root.getBoundingClientRect(), e.clientX, e.clientY);
    if (outside !== cancelArmedRef.current) {
      cancelArmedRef.current = outside;
      setWillCancel(outside);
    }
  }

  function onPointerUp(e: React.PointerEvent) {
    if (!holdingRef.current) return;
    holdingRef.current = false;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const cancel = cancelArmedRef.current;
    cancelArmedRef.current = false;
    setWillCancel(false);
    if (recorder.kind !== 'recording') {
      pendingCancelRef.current = true;
      return;
    }
    if (cancel) cancelRecording();
    else void finishAndEmit();
  }

  return {
    recorder,
    willCancel,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  };
}
